import { ApolloError } from '@apollo/client';

import {
  useGroupFinanceContributionsQuery,
  GroupFinanceContributionsQuery as GroupFinanceContributionsDoc,
} from 'core/graphql';
import type { IGroupFinanceAgreement } from 'core/utils/groupMoney';

import { useStableRefetch } from './useStableRefetch';

/* ---------- lazy contract-side contributions (FinancesTab / AlertsPanel) ---------- */

/**
 * One agreement node as returned by `GroupFinanceContributionsQuery`
 * (`group.agreements[]`): `{ id, currency, budgetAmount, paidAmount,
 * terminated, …, scheduleContributions { id date amountForeign complete
 * remainder delay coveredAmount debt status } }`.
 *
 * This generated node is structurally a superset of `IGroupFinanceAgreement`
 * (from `core/utils/groupMoney`): `currency` (`AgreementCurrency` string enum)
 * is assignable to `string`, `terminated` (`boolean`) to `boolean | null`, and
 * the `scheduleContributions` list (ordered by date, then id, on the server)
 * matches the structural amountForeign/coveredAmount/debt/complete/date shape
 * the util consumes. So the nodes feed straight into `buildMoneyModel` with
 * **no remapping** — this hook stays a thin pass-through and just narrows the
 * typing to the util's interface.
 */
type IGroupFinanceContributionsAgreementNode = NonNullable<
  NonNullable<GroupFinanceContributionsDoc['group']>['agreements'][number]
>;

/** `refetch` выключенного запроса — пустышка: под skip Apollo 3.3 ушёл бы в сеть. */
const SKIPPED_REFETCH = (): void => undefined;

/**
 * Result of {@link useGroupFinanceContributions}. `agreements` is typed as the
 * shape `buildMoneyModel` (from `core/utils/groupMoney`) consumes, so callers
 * can pass it straight through.
 */
export interface IUseGroupFinanceContributions {
  /** Active + terminated agreements, each carrying its full `contributions`. */
  agreements: IGroupFinanceAgreement[];
  /** True during initial load AND background refresh. */
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Lazy contract-side detail source for the FinancesTab and AlertsPanel. It
 * carries the full per-agreement payment schedule (`scheduleContributions`)
 * that used to bloat the eager `GroupDetailQuery`, fetched only on demand.
 *
 * "Lazy" here is **component-level** laziness: the hook is only mounted by the
 * FinancesTab / AlertsPanel when they actually need contract-side figures,
 * consistent with the other per-tab hooks in `useGroupTabsData.ts`. There is no
 * explicit user-triggered fetch in the design, so this mirrors those hooks —
 * the standard query hook gated by `skip: !groupId` rather than Apollo's
 * `useLazyQuery`. `cache-and-network` + `notifyOnNetworkStatusChange` keep
 * `loading` true during background refresh.
 */
export const useGroupFinanceContributions = (
  groupId?: string,
  /** Роль без `MANAGEMENT_ROLES` (`canReadFinanceContributions`) — не запрашивать. */
  skip?: boolean,
): IUseGroupFinanceContributions => {
  const skipped = !groupId || Boolean(skip);
  const { data, loading, error, refetch } = useGroupFinanceContributionsQuery({
    variables: { groupId: groupId || '' },
    skip: skipped,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const agreements: IGroupFinanceAgreement[] = (
    data?.group?.agreements ?? []
  ).filter((agreement): agreement is IGroupFinanceContributionsAgreementNode =>
    Boolean(agreement),
  );

  return {
    agreements,
    loading,
    error,
    refetch: useStableRefetch(skipped ? SKIPPED_REFETCH : refetch),
  };
};
